import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, IconArrowLeft, Notice, Plate, SelectField, TextField, type ListColumn } from "@wilco/ui";
import { useState } from "react";
import { CatalogPick, skillLabel } from "../catalog/DirectoryLists.tsx";
import type { Product } from "../catalog/api.ts";
import { DocumentList } from "../docs/DocumentList.tsx";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { serverPage } from "../docs/serverPage.ts";
import { formatNumber, t, type MessageKey } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { cardsApi, type CardDetail, type CardRow, type CardStep, type CardStepEdit } from "./cardsApi.ts";
import "./cards.css";

/*
 * Технологические карты мастера дільниці (RTE-01, RTE-02, RTE-20): одна
 * активная на изделие и тип деятельности, хвост «Прошивка», «Тест»,
 * «Упаковка» последним, у R&D без «Упаковки». Новая карта: шаги сборки,
 * хвост добавляет сервер. Правка карты целиком, хвост тоже (навык «Тесту»,
 * шаг возврата); проверку делает сервер, правка идёт на новые серийники.
 */

const CARDS = ["route", "cards"] as const;

const ACTIVITY: Record<string, MessageKey> = { SER: "cards.activity.SER", RND: "cards.activity.RND" };
type StepKind = "WORK" | "FIRMWARE" | "TEST" | "PACKING";
const KIND: Record<StepKind, MessageKey> = {
  WORK: "cards.kind.WORK",
  FIRMWARE: "cards.kind.FIRMWARE",
  TEST: "cards.kind.TEST",
  PACKING: "cards.kind.PACKING",
};
const kindLabel = (k: string) => (k in KIND ? t(KIND[k as keyof typeof KIND]) : k);

const listRoute = getRouteApi("/app/production/cards");

export function RouteCardsPage() {
  const search = listRoute.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<CardRow>[] = [
    { key: "name", header: t("cards.name"), kind: "title", render: (c) => c.name },
    { key: "product", header: t("directory.product"), kind: "text", render: (c) => c.productName },
    { key: "activity", header: t("cards.activity"), kind: "text", width: "8rem", render: (c) => t(ACTIVITY[c.activity] ?? "cards.activity.SER") },
    { key: "steps", header: t("cards.steps"), kind: "number", width: "5rem", render: (c) => formatNumber(c.stepCount) },
    { key: "state", header: t("docs.status"), kind: "text", width: "7rem", render: (c) => (c.active ? <Badge tone="success">{t("directory.active")}</Badge> : <Badge>{t("catalog.archived")}</Badge>) },
  ];
  return (
    <DocumentList
      title={t("cards.title")}
      icon="route"
      queryKey={[...CARDS, "list"]}
      load={async (q) => serverPage(await cardsApi.list({ ...q, active: q.status === "active" ? true : q.status === "archived" ? false : null }))}
      columns={columns}
      rowKey={(c) => String(c.id)}
      onOpen={(c) => void navigate({ to: "/production/cards/$id", params: { id: String(c.id) } })}
      statuses={[
        { value: "active", label: t("directory.active") },
        { value: "archived", label: t("catalog.archived") },
      ]}
      sorts={[{ value: "created", label: t("docs.sort.created") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/production/cards", search: next, replace: true })}
      searchHint={t("cards.searchHint")}
      create={{ label: t("cards.new"), to: "/production/cards/new" }}
      empty={{ title: t("cards.empty"), body: t("cards.emptyBody") }}
    />
  );
}

/** Строка шага в редакторе: версии строкой через запятую, остальное как у сервера. */
interface StepDraft extends Omit<CardStepEdit, "versions"> {
  versions: string;
}

const draftOf = (s: CardStep): StepDraft => ({ ...s, skill: s.skill ?? "", returnTo: s.returnTo ?? null, versions: s.versions.join(", ") });
const editOf = (s: StepDraft): CardStepEdit => ({
  ...s,
  name: s.name.trim(),
  skill: s.skill?.trim() || null,
  versions: s.versions.split(",").map((v) => v.trim()).filter(Boolean),
});

function StepsEditor({ steps, onChange, full }: { steps: StepDraft[]; onChange: (next: StepDraft[]) => void; full: boolean }) {
  const set = (i: number, patch: Partial<StepDraft>) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const add = () => {
    // Новый шаг сборки встаёт перед хвостом: номер на 10 больше последнего шага сборки.
    const work = steps.filter((s) => s.kind === "WORK");
    const sequence = (work.at(-1)?.sequence ?? 0) + 10;
    const at = full ? steps.findIndex((s) => s.kind !== "WORK") : -1;
    const step: StepDraft = { sequence, name: "", kind: "WORK", skill: "", required: true, parallel: false, holdMinutes: 0, qualityCheck: false, returnTo: null, wholeBatch: false, versions: "" };
    onChange(at < 0 ? [...steps, step] : [...steps.slice(0, at), step, ...steps.slice(at)]);
  };
  return (
    <div className="card-steps">
      <ol className="card-steps-list">
        {steps.map((s, i) => (
          <li key={i} className="card-step">
            <div className="card-step-head">
              <span className="w-num card-step-seq">{s.sequence}</span>
              <span className="w-muted">{kindLabel(s.kind)}</span>
              {s.kind === "WORK" ? (
                <Button variant="ghost" onClick={() => onChange(steps.filter((_, j) => j !== i))} aria-label={t("cards.removeStep", { step: s.name || String(s.sequence) })}>
                  {t("cards.remove")}
                </Button>
              ) : null}
            </div>
            <div className="card-step-fields">
              <TextField label={t("cards.stepName")} value={s.name} onChange={(e) => set(i, { name: e.target.value })} />
              <TextField label={t("cards.sequence")} type="number" inputMode="numeric" value={String(s.sequence)} onChange={(e) => set(i, { sequence: Number(e.target.value) })} />
              <TextField label={t("directory.skill")} hint={s.skill ? skillLabel(s.skill) : undefined} value={s.skill ?? ""} onChange={(e) => set(i, { skill: e.target.value })} />
              {s.kind === "WORK" ? (
                <>
                  <TextField label={t("cards.versions")} hint={t("cards.versionsHint")} value={s.versions} onChange={(e) => set(i, { versions: e.target.value })} />
                  <TextField label={t("cards.hold")} type="number" inputMode="numeric" value={String(s.holdMinutes)} onChange={(e) => set(i, { holdMinutes: Number(e.target.value) })} />
                </>
              ) : null}
              {s.kind === "TEST" || s.qualityCheck ? (
                <SelectField
                  label={t("cards.returnTo")}
                  value={s.returnTo == null ? "" : String(s.returnTo)}
                  onChange={(e) => set(i, { returnTo: e.target.value ? Number(e.target.value) : null })}
                  options={[{ value: "", label: t("cards.returnNone") }, ...steps.filter((o) => o.sequence < s.sequence).map((o) => ({ value: String(o.sequence), label: `${String(o.sequence)} ${o.name}` }))]}
                />
              ) : null}
            </div>
            {s.kind === "WORK" ? (
              <div className="card-step-flags">
                <label className="check">
                  <input type="checkbox" checked={s.required} onChange={(e) => set(i, { required: e.target.checked })} />
                  {t("cards.required")}
                </label>
                <label className="check">
                  <input type="checkbox" checked={s.parallel} onChange={(e) => set(i, { parallel: e.target.checked })} />
                  {t("cards.parallel")}
                </label>
                <label className="check">
                  <input type="checkbox" checked={s.wholeBatch} onChange={(e) => set(i, { wholeBatch: e.target.checked })} />
                  {t("cards.wholeBatch")}
                </label>
                {full ? (
                  <label className="check">
                    <input type="checkbox" checked={s.qualityCheck} onChange={(e) => set(i, { qualityCheck: e.target.checked })} />
                    {t("cards.qualityCheck")}
                  </label>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      <Button variant="secondary" onClick={add}>
        {t("cards.addStep")}
      </Button>
    </div>
  );
}

interface NewCard {
  product: Product | null;
  name: string;
  activity: string;
  strictOrder: boolean;
  steps: StepDraft[];
}

const blankStep = (sequence: number): StepDraft => ({ sequence, name: "", kind: "WORK", skill: "", required: true, parallel: false, holdMinutes: 0, qualityCheck: false, returnTo: null, wholeBatch: false, versions: "" });

/** Новая карта: шаги сборки, хвост «Прошивка», «Тест», «Упаковка» сервер добавляет сам (RTE-20). */
export function NewRouteCardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useDocumentForm<NewCard, CardDetail | { id: number }>({
    initial: { product: null, name: "", activity: "SER", strictOrder: true, steps: [blankStep(10)] },
    validate: (v) => ({
      product: v.product ? undefined : t("docs.required"),
      name: v.name.trim() ? undefined : t("docs.required"),
      steps: v.steps.some((s) => s.name.trim()) ? undefined : t("cards.stepsRequired"),
    }),
    submit: (v, key) =>
      cardsApi.define(
        {
          productId: v.product?.id ?? "",
          name: v.name.trim(),
          activity: v.activity,
          strictOrder: v.strictOrder,
          steps: v.steps.filter((s) => s.name.trim()).map((s) => {
            const e = editOf(s);
            return { sequence: e.sequence, name: e.name, skill: e.skill, required: e.required, parallel: e.parallel, holdMinutes: e.holdMinutes, wholeBatch: e.wholeBatch, versions: e.versions };
          }),
        },
        key,
      ),
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: CARDS });
      void navigate({ to: "/production/cards/$id", params: { id: String(c.id) }, replace: true });
    },
    fieldOf: (code) => (code === "card.active_exists" ? "product" : code.startsWith("card.") ? "steps" : undefined),
  });
  const { values, set, errors } = form;
  return (
    <FormPage title={t("cards.new")} back="/production/cards" submitLabel={t("catalog.create")} form={form}>
      <CatalogPick label={t("directory.product")} value={values.product} onChange={(p) => set("product", p)} error={errors.product} products />
      <TextField label={t("cards.name")} value={values.name} onChange={(e) => set("name", e.target.value)} error={errors.name} />
      <SelectField
        label={t("cards.activity")}
        value={values.activity}
        onChange={(e) => set("activity", e.target.value)}
        options={Object.entries(ACTIVITY).map(([value, key]) => ({ value, label: t(key) }))}
      />
      <label className="check">
        <input type="checkbox" checked={values.strictOrder} onChange={(e) => set("strictOrder", e.target.checked)} />
        {t("cards.strictOrder")}
      </label>
      {errors.steps ? <Notice tone="danger" title={errors.steps} /> : null}
      <StepsEditor steps={values.steps} onChange={(steps) => set("steps", steps)} full={false} />
      <p className="w-muted">{t("cards.tailHint")}</p>
    </FormPage>
  );
}

const cardRoute = getRouteApi("/app/production/cards/$id");

/** Карта целиком: шаги по версии изделия, правка всех шагов с хвостом, архив. */
export function RouteCardPage() {
  const { id } = cardRoute.useParams();
  const cardId = Number(id);
  const queryClient = useQueryClient();
  const [version, setVersion] = useState("");
  const card = useQuery({ queryKey: [...CARDS, cardId, version], queryFn: () => cardsApi.card(cardId, version || undefined), retry: false });
  const [draft, setDraft] = useState<{ name: string; strictOrder: boolean; steps: StepDraft[] } | null>(null);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const save = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => cardsApi.edit(cardId, { name: d.name.trim(), strictOrder: d.strictOrder, steps: d.steps.map(editOf) }, key),
    onSuccess: () => {
      setDraft(null);
      setKey(crypto.randomUUID());
      void queryClient.invalidateQueries({ queryKey: CARDS });
    },
  });
  const archive = useMutation({ mutationFn: () => cardsApi.archive(cardId), onSuccess: () => void queryClient.invalidateQueries({ queryKey: CARDS }) });
  const c = card.data;
  const versions = [...new Set(c?.steps.flatMap((s) => s.versions) ?? [])];
  const editDraft = (next: NonNullable<typeof draft>) => {
    setDraft(next);
    setKey(crypto.randomUUID());
  };

  return (
    <div className="page doc-form route-card">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/production/cards" aria-label={t("docs.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title">{c?.name ?? t("cards.title")}</h1>
        {c?.active === false ? <Badge>{t("catalog.archived")}</Badge> : null}
      </div>
      {card.error ? <Notice tone="danger" title={errorNotice(card.error).title} className="stock-notice" /> : null}
      {save.error ? <Notice tone="danger" title={errorNotice(save.error).title} className="stock-notice" /> : null}
      {archive.error ? <Notice tone="danger" title={errorNotice(archive.error).title} className="stock-notice" /> : null}
      {c ? (
        <Plate cut="lg">
          <p className="route-card-meta">
            {c.productName} · {t(ACTIVITY[c.activity] ?? "cards.activity.SER")} · {c.strictOrder ? t("cards.strictOrder") : t("cards.freeOrder")}
          </p>
          {draft ? (
            <form
              className="stock-form"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(draft);
              }}
            >
              <TextField label={t("cards.name")} value={draft.name} onChange={(e) => editDraft({ ...draft, name: e.target.value })} />
              <label className="check">
                <input type="checkbox" checked={draft.strictOrder} onChange={(e) => editDraft({ ...draft, strictOrder: e.target.checked })} />
                {t("cards.strictOrder")}
              </label>
              <StepsEditor steps={draft.steps} onChange={(steps) => editDraft({ ...draft, steps })} full />
              <p className="w-muted">{t("cards.editHint")}</p>
              <div className="doc-form-actions">
                <Button type="submit" variant="primary" disabled={save.isPending}>
                  {t("cards.save")}
                </Button>
                <Button variant="ghost" onClick={() => setDraft(null)}>
                  {t("docs.cancel")}
                </Button>
              </div>
            </form>
          ) : (
            <>
              {versions.length > 0 ? (
                <SelectField
                  label={t("cards.forVersion")}
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  options={[{ value: "", label: t("cards.allVersions") }, ...versions.map((v) => ({ value: v, label: v }))]}
                />
              ) : null}
              <ol className="card-view">
                {c.steps.map((s) => (
                  <li key={s.sequence} className="card-view-step">
                    <span className="w-num card-step-seq">{s.sequence}</span>
                    <span className="card-view-name">{s.name}</span>
                    <span className="w-muted">
                      {[
                        kindLabel(s.kind),
                        s.skill ? skillLabel(s.skill) : null,
                        s.parallel ? t("cards.parallel") : null,
                        s.required ? null : t("cards.optional"),
                        s.wholeBatch ? t("cards.wholeBatch") : null,
                        s.holdMinutes ? t("cards.holdShort", { minutes: s.holdMinutes }) : null,
                        s.returnTo != null ? t("cards.returnShort", { step: s.returnTo }) : null,
                        s.versions.length ? s.versions.join(", ") : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                ))}
              </ol>
              {c.active ? (
                <div className="doc-form-actions">
                  <Button variant="primary" onClick={() => editDraft({ name: c.name, strictOrder: c.strictOrder, steps: c.steps.map(draftOf) })} disabled={version !== ""}>
                    {t("cards.edit")}
                  </Button>
                  <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate()}>
                    {t("directory.toArchive")}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Plate>
      ) : null}
    </div>
  );
}
